import { zoomViewport, type ZoomView } from './videoZoom';

/**
 * "Frame" around the video in the editor (preview and export): background, padding, rounded
 * corners and shadow. Annotations are drawn on top afterwards and keep their positions on the
 * full picture.
 */
export type FrameBackground = 'none' | 'color' | 'gradient' | 'blur';

export interface VideoFrameStyle {
  background: FrameBackground;
  /** Solid colour (background 'color'). */
  color: string;
  /** Id from FRAME_GRADIENTS (background 'gradient'). */
  gradient: string;
  /** Space around the video, as a share of the shorter side (0 – 0.2). */
  padding: number;
  /** Corner radius, as a share of the shorter side of the video (0 – 0.1). */
  radius: number;
  shadow: boolean;
}

export const FRAME_GRADIENTS: { id: string; from: string; to: string }[] = [
  { id: 'indigo', from: '#4f46e5', to: '#a855f7' },
  { id: 'ocean', from: '#0ea5e9', to: '#1e3a8a' },
  { id: 'sunset', from: '#f97316', to: '#db2777' },
  { id: 'mint', from: '#10b981', to: '#0f766e' },
  { id: 'slate', from: '#334155', to: '#0f172a' },
];

export const FRAME_COLORS = ['#ffffff', '#f1f5f9', '#111827', '#1e3a8a', '#fef3c7'];

export const DEFAULT_FRAME: VideoFrameStyle = {
  background: 'none',
  color: '#f1f5f9',
  gradient: 'indigo',
  padding: 0,
  radius: 0,
  shadow: false,
};

export const FRAME_LIMITS = { padding: 0.2, radius: 0.1 };

const clamp = (value: unknown, max: number) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(max, Math.max(0, n)) : 0;
};

/** Accepts a stored value (draft) and returns a valid style; defaults for anything missing. */
export function normalizeFrame(value: unknown): VideoFrameStyle {
  const v = (value && typeof value === 'object' ? value : {}) as Partial<VideoFrameStyle>;
  return {
    background: (['none', 'color', 'gradient', 'blur'] as const).includes(v.background as FrameBackground)
      ? (v.background as FrameBackground)
      : 'none',
    color: typeof v.color === 'string' && /^#[0-9a-f]{6}$/i.test(v.color) ? v.color : DEFAULT_FRAME.color,
    gradient: FRAME_GRADIENTS.some((g) => g.id === v.gradient) ? (v.gradient as string) : DEFAULT_FRAME.gradient,
    padding: clamp(v.padding, FRAME_LIMITS.padding),
    radius: clamp(v.radius, FRAME_LIMITS.radius),
    shadow: v.shadow === true,
  };
}

/** False when the frame changes nothing (the video fills the picture as before). */
export const isFrameActive = (f: VideoFrameStyle) =>
  f.padding > 0 || f.radius > 0 || f.shadow || f.background !== 'none';

/** What the video can be drawn from: the <video>, a canvas, or a decoded frame. */
export type Source = CanvasImageSource & {
  width?: number; height?: number; videoWidth?: number; videoHeight?: number;
  /** Decoded frames (WebCodecs VideoFrame) report their size here. */
  displayWidth?: number; displayHeight?: number;
};

/**
 * The largest rectangle of proportions sw:sh centred in the box. A box that already has those
 * proportions (within 1%, e.g. rounding) is returned as is, so nothing changes in the normal case.
 */
export function containRect(sw: number, sh: number, x: number, y: number, w: number, h: number) {
  if (!sw || !sh || !w || !h || Math.abs((sw / sh) / (w / h) - 1) < 0.01) return { x, y, w, h };
  const s = Math.min(w / sw, h / sh);
  return { x: x + (w - sw * s) / 2, y: y + (h - sh * s) / 2, w: sw * s, h: sh * s };
}

/**
 * Draws the source into the given box; with a zoom, only the zoomed part of it. A source of other
 * proportions than the box is contained (black bars), never stretched.
 */
function drawSource(
  ctx: CanvasRenderingContext2D,
  source: Source,
  x: number,
  y: number,
  w: number,
  h: number,
  zoom: ZoomView | null,
) {
  const sw = source.videoWidth || source.displayWidth || source.width || 0;
  const sh = source.videoHeight || source.displayHeight || source.height || 0;
  const r = containRect(sw, sh, x, y, w, h);
  if (r.w !== w || r.h !== h) {
    ctx.fillStyle = '#000000';
    ctx.fillRect(x, y, w, h);
  }
  if (!zoom || !sw || !sh) {
    ctx.drawImage(source, r.x, r.y, r.w, r.h);
    return;
  }
  const v = zoomViewport(zoom);
  ctx.drawImage(source, v.left * sw, v.top * sh, v.width * sw, v.height * sh, r.x, r.y, r.w, r.h);
}

let blurCanvas: HTMLCanvasElement | null = null;

function drawBackground(ctx: CanvasRenderingContext2D, source: Source, W: number, H: number, f: VideoFrameStyle) {
  if (f.background === 'color') {
    ctx.fillStyle = f.color;
    ctx.fillRect(0, 0, W, H);
  } else if (f.background === 'gradient') {
    const g = FRAME_GRADIENTS.find((x) => x.id === f.gradient) ?? FRAME_GRADIENTS[0];
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, g.from);
    grad.addColorStop(1, g.to);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
  } else if (f.background === 'blur') {
    // Cheap blur: draw the frame tiny, then scale it up smoothly; darken slightly.
    blurCanvas ??= document.createElement('canvas');
    blurCanvas.width = 48;
    blurCanvas.height = Math.max(1, Math.round((48 * H) / W));
    const bctx = blurCanvas.getContext('2d');
    if (bctx) {
      bctx.drawImage(source, 0, 0, blurCanvas.width, blurCanvas.height);
      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(blurCanvas, 0, 0, W, H);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }
  } else {
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, W, H);
  }
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
}

/** Where the video sits in a W×H picture with this frame style (the whole picture without one). */
export function videoBox(W: number, H: number, f: VideoFrameStyle) {
  if (!isFrameActive(f)) return { x: 0, y: 0, w: W, h: H };
  // Scale the video down by the padding while keeping its proportions (W×H is the video's
  // aspect ratio), and centre it.
  const pad = f.padding * Math.min(W, H);
  let vw = Math.max(1, W - pad * 2);
  let vh = (vw * H) / W;
  if (vh > H - pad * 2) {
    vh = Math.max(1, H - pad * 2);
    vw = (vh * W) / H;
  }
  return { x: (W - vw) / 2, y: (H - vh) / 2, w: vw, h: vh };
}

/**
 * Draws the video frame into a W×H picture with the given frame style and optional zoom (the
 * zoom enlarges the video inside its frame). Without either it is the plain full-size draw.
 */
export function drawFramedVideo(
  ctx: CanvasRenderingContext2D,
  source: Source,
  W: number,
  H: number,
  f: VideoFrameStyle,
  zoom: ZoomView | null = null,
) {
  if (!isFrameActive(f)) {
    drawSource(ctx, source, 0, 0, W, H, zoom);
    return;
  }
  drawBackground(ctx, source, W, H, f);

  const { x, y, w: vw, h: vh } = videoBox(W, H, f);
  const r = f.radius * Math.min(vw, vh);

  ctx.save();
  if (f.shadow) {
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
    ctx.shadowBlur = Math.max(8, Math.min(W, H) * 0.04);
    ctx.shadowOffsetY = Math.max(2, Math.min(W, H) * 0.012);
    ctx.fillStyle = '#000000';
    roundedRect(ctx, x, y, vw, vh, r);
    ctx.fill();
    ctx.restore();
  }
  roundedRect(ctx, x, y, vw, vh, r);
  ctx.clip();
  drawSource(ctx, source, x, y, vw, vh, zoom);
  ctx.restore();
}
